// Canonical unrealized P/L and quote-price resolution for open positions.
// Shared by Dashboard and StockSummary so both views value positions
// identically.
//
// Position conventions (matching /api/accounts/:id/positions normalization):
//   quantity    — signed; negative = short
//   cost_basis  — signed; negative = credit received (short)
//   marketValue — positive magnitude (broker-reported mark value)
//   price       — current per-share / per-contract price (positive)
//
// Formula: short → |cost| - marketValue (proceeds − liability)
//          long  → marketValue - cost

const toPositive = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Heuristic option detection shared across broker symbol formats:
// OCC ("AAPL 250117C00150000" / "AAPL_250117C00150000"), descriptive
// ("AAPL 01/17/25 $150 Call"), and Schwab-style symbols with spaces.
export const isOptionLike = (symbol, isOptionFlag) => {
  if (isOptionFlag !== undefined && isOptionFlag !== null) return !!isOptionFlag;
  const s = String(symbol || '');
  return s.includes('_')
    || /[A-Z]+\d{6}[CP]\d+/i.test(s)
    || /\d{1,2}\/\d{1,2}\/\d{2,4}\s+\$?[\d.]+\s+(call|put)/i.test(s)
    || (/\b(call|put)\b/i.test(s) && /\d{6}/.test(s));
};

// Resolve a usable mark price from a broker quote object.
// Priority: explicit mid/mark → bid/ask midpoint → last-trade variants.
// Mid is preferred over last because illiquid options often have stale
// last-trade prices far from fair value (this is what StockSummary uses).
export const resolveQuotePrice = (quote) => {
  if (!quote) return 0;
  const mid = toPositive(quote.mid);
  if (mid) return mid;
  const mark = toPositive(quote.mark ?? quote.mark_price ?? quote.markPrice);
  if (mark) return mark;
  const bid = toPositive(quote.bid ?? quote.bidPrice);
  const ask = toPositive(quote.ask ?? quote.askPrice);
  if (bid && ask) return (bid + ask) / 2;
  if (bid || ask) return bid || ask;
  return toPositive(quote.last ?? quote.lastPrice ?? quote.last_trade_price ?? quote.price);
};

// Canonical unrealized P/L for one open position/leg.
// `position` may carry quantity, cost_basis/costBasis/cost,
// marketValue/market_value, symbol, isOption.
// `quote` (optional) — when it resolves to a usable price, market value is
// derived from it; otherwise the position's own marketValue is the fallback.
// Returns 0 when the position cannot be valued at all.
export const positionUnrealizedPL = (position, quote) => {
  const qty = parseFloat(position?.quantity ?? 0) || 0;
  const cost = parseFloat(position?.cost_basis ?? position?.costBasis ?? position?.cost ?? 0) || 0;
  const symbol = position?.symbol ?? position?.instrument?.symbol ?? '';
  const isOption = isOptionLike(symbol,
    position?.isOption ?? (position?.instrument?.assetType ? position.instrument.assetType === 'OPTION' : undefined));
  // Futures options carry their own premium multiplier (e.g. /MES = 5), read
  // from the Schwab instrument's optionPremiumMultiplier — not 100.
  const mult = position?.contractMultiplier || position?.optionInfo?.multiplier || (isOption ? 100 : 1);
  const absQty = Math.abs(qty);
  const isShort = qty < 0 || (qty === 0 && cost < 0);

  const price = resolveQuotePrice(quote);
  let marketValue;
  if (price > 0 && absQty > 0) {
    marketValue = price * absQty * mult;
  } else if (quote) {
    // A quote exists but has no usable price — treat as marked to zero.
    marketValue = 0;
  } else {
    marketValue = Math.abs(parseFloat(position?.marketValue ?? position?.market_value ?? 0) || 0);
    if (marketValue <= 0) return 0; // unpriceable — contributes nothing
  }

  return isShort ? Math.abs(cost) - marketValue : marketValue - cost;
};
